'use strict';

/**
 * /ts/util.ts - foundation for every other client script: the view registry,
 * the api()/requireEl()/reportError() primitives, and small pure helpers.
 * Must load first (see lib/Assets.php's script order) since every other
 * file calls requireEl()/api() at its own top level to wire up its DOM
 * listeners. registerView() is the extension point for a new tab: register
 * a load function keyed by the same id used in lib/Views.php and the nav
 * radio's data-tab attribute.
 */

const views: Record<string, View> = {};
let activeTab = 'dashboard';

function registerView(id: string, view: View): void {
	views[id] = view;
}

function csrfToken(): string {
	return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
}

function centsToUsd(cents: number): string {
	return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** Sign carries meaning wherever an amount appears in the register, so
 * the class that colors it is derived the same way everywhere. */
function amountClass(amount: number): string {
	return amount < 0 ? 'amount-neg' : amount > 0 ? 'amount-pos' : '';
}

const HTML_ESCAPES: Record<string, string> = {
	'&': '&amp;',
	'<': '&lt;',
	'>': '&gt;',
	'"': '&quot;',
	"'": '&#39;'
};

function escapeHtml(value: string): string {
	return value.replace(/[&<>"']/g, c => HTML_ESCAPES[c] ?? c);
}

/**
 * Hand a client-side-built Blob to the browser as a file download. The
 * createObjectURL -> hidden anchor click -> revokeObjectURL dance is
 * identical at every export call site (Report CSV, Rules export, Business
 * backup), so it lives here rather than three times over; the URL is revoked
 * immediately after click(), which is safe because the browser has already
 * captured it for the in-flight download.
 */
function downloadBlob(filename: string, blob: Blob): void {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
}

/** SimpleFIN repeats the same warning once per chunked request, so the UI
 * shows one copy of each distinct message. */
function simpleFinErrorsText(errors?: { code: string; msg: string }[]): string {
	const seen = new Set<string>();
	const msgs: string[] = [];
	for (const e of errors ?? []) {
		const key = `${e.code}: ${e.msg}`;
		if (seen.has(key)) continue;
		seen.add(key);
		msgs.push(e.msg);
	}
	return msgs.length ? `SimpleFIN: ${msgs.join('; ')}` : '';
}

/**
 * One line describing an ingest result, shared by Sync Now and Import so the
 * two cannot diverge. Fetched counts records the bank returned for the
 * requested accounts; inserted counts the ones that were not already in the
 * ledger, so "all already imported" is the normal idle-sync outcome rather
 * than a failure. Zero-value counts are omitted as noise.
 */
function ingestSummary(res: {
	fetched?: number;
	inserted?: number;
	merged?: number;
	skipped?: number;
	categorized?: number;
	requests_today?: number;
	requests_quota?: number;
}): string {
	const fetched = res.fetched ?? 0;
	const inserted = res.inserted ?? 0;
	const merged = res.merged ?? 0;
	const parts: string[] = [];

	if (fetched === 0) {
		parts.push('Nothing fetched.');
	} else if (inserted === 0 && merged === 0) {
		parts.push(`${fetched} fetched, all already imported.`);
	} else {
		parts.push(`${fetched} fetched, ${inserted} new.`);
	}
	// A cross-source match (this same transaction already exists under a
	// CSV backfill's own id, or vice versa) merges into the existing row
	// instead of duplicating it -- worth surfacing.
	if (merged) parts.push(`${merged} matched existing record${merged === 1 ? '' : 's'} from another source.`);
	if (res.skipped) parts.push(`${res.skipped} pending skipped.`);
	if (res.categorized) parts.push(`${res.categorized} categorized by rule.`);
	// A running count of real SimpleFIN requests made today, against its own
	// documented approximate quota (Ingest::DAILY_REQUEST_QUOTA) -- not a
	// hard cap we enforce, just visibility, so the short Sync Now cooldown
	// (60s, was 300s) doesn't have to be the only thing standing between
	// rapid re-clicking and quietly burning through the day's real quota. The
	// wording escalates once it's actually close, rather than reading the
	// same at 3 requests and at 23.
	if (res.requests_today !== undefined && res.requests_quota !== undefined) {
		const close = res.requests_today >= res.requests_quota * 0.8;
		parts.push(
			`${res.requests_today} of ~${res.requests_quota} SimpleFIN requests today.` +
				(close ? ' Approaching the daily limit.' : '')
		);
	}

	return parts.join(' ');
}

/** WebAuthn's binary fields (challenge, credential id, etc.) travel as
 * base64url over the wire (matching lib/Passkey.php's b64uEncode/Decode) but
 * the browser's credentials API wants real ArrayBuffers -- these two convert
 * between the two, shared by ts/login.ts and ts/security.ts. */
function b64uToBuf(b64u: string): ArrayBuffer {
	const pad = b64u.length % 4;
	const b64 = (b64u + '='.repeat(pad ? 4 - pad : 0)).replace(/-/g, '+').replace(/_/g, '/');
	const bin = atob(b64);
	const buf = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
	return buf.buffer;
}

function bufToB64u(buf: ArrayBuffer): string {
	let bin = '';
	for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b);
	return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Shared by ts/login.ts and ts/security.ts, whose WebAuthn ceremonies both
 * need the same feature check and must report it the same way. */
const PASSKEY_UNSUPPORTED_MESSAGE = 'This browser does not support passkeys';

function passkeysSupported(): boolean {
	return !!window.PublicKeyCredential;
}

function toStringRecord(params: Record<string, string | number>): Record<string, string> {
	const out: Record<string, string> = {};
	for (const [k, v] of Object.entries(params)) out[k] = String(v);
	return out;
}

/** Seconds before the idle logout that the warning toast appears. */
const IDLE_WARNING_S = 60;

/** lib/Auth.php rewrites the session's last_seen at most once a minute, so
 * pinging more often than that on interaction would change nothing. */
const ACTIVITY_PING_MS = 60_000;

/** Any of these anywhere on the page counts as the operator being present
 * (pointerdown covers touch and scrollbar drags). Deliberately not
 * pointermove/scroll: browsers fire those for layout shifts under a resting
 * cursor and for programmatic scrolls, so the warning toast appearing (or a
 * Dashboard auto-reload) could reset the clock with nobody there. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel'] as const;

let idleWarningTimer: number | undefined;
let idleLogoutTimer: number | undefined;
let releaseIdleWarning: (() => void) | null = null;
let activityListening = false;
let lastActivityPingAt = 0;

/** Throttled keepalive on interaction; its response re-arms the timers
 * (and so retracts an open warning) through api() like any other. */
function onActivity(): void {
	const now = Date.now();
	if (now - lastActivityPingAt < ACTIVITY_PING_MS) return;
	lastActivityPingAt = now;
	void api('/api/auth/keepalive', {}, 'POST').catch(() => {});
}

function redirectToLogin(): void {
	location.href = `/login?next=${encodeURIComponent(location.pathname + location.search + location.hash)}`;
}

/**
 * (Re)arm the idle warning and logout off the server's own remaining-idle
 * count (X-Session-Idle-Remaining, lib/Auth.php), which every authenticated
 * response carries -- background polls included, since they report the real
 * figure without extending it. A response showing the clock was pushed back
 * past the warning window (any real request, including an interaction's
 * keepalive) retracts an open warning. The first call installs the
 * page-wide activity listener -- only a logged-in page ever gets here, so
 * the login page (which shares this file) never pings.
 */
function armIdleTimers(remainingS: number): void {
	if (!Number.isFinite(remainingS)) return;
	if (!activityListening) {
		activityListening = true;
		for (const type of ACTIVITY_EVENTS) {
			// Capture phase: a handler that stops propagation can't hide it.
			document.addEventListener(type, onActivity, { capture: true, passive: true });
		}
	}
	window.clearTimeout(idleWarningTimer);
	window.clearTimeout(idleLogoutTimer);
	if (remainingS > IDLE_WARNING_S && releaseIdleWarning) {
		releaseIdleWarning();
		releaseIdleWarning = null;
	}
	idleWarningTimer = window.setTimeout(showIdleWarning, Math.max(0, remainingS - IDLE_WARNING_S) * 1000);
	idleLogoutTimer = window.setTimeout(idleLogout, remainingS * 1000);
}

/** Persistent, so it can't fade unseen; any interaction (onActivity())
 * resets the clock and armIdleTimers() then retracts it. */
function showIdleWarning(): void {
	if (releaseIdleWarning) return;
	releaseIdleWarning = showToast(
		`You will be signed out in ${IDLE_WARNING_S} seconds due to inactivity. Any activity keeps you signed in.`,
		{ persist: true }
	);
}

/** Logs out server-side before redirecting: the client timer can land a
 * moment before the server's own expiry, and /login bounces a still-valid
 * session straight back to the app. */
function idleLogout(): void {
	void fetch('/api/auth/logout', {
		method: 'POST',
		headers: { 'X-CSRF-Token': csrfToken() },
		cache: 'no-store'
	})
		.catch(() => {})
		.finally(redirectToLogin);
}

/** background: a request the operator didn't initiate (a poll), which must
 * not count as session activity -- see lib/Auth.php's userId(). */
async function api<T>(
	path: string,
	params: Record<string, string | number> = {},
	method: 'GET' | 'POST' = 'GET',
	opts: { background?: boolean } = {}
): Promise<T> {
	const url = new URL(path, location.href);
	let body: URLSearchParams | null = null;
	const headers: Record<string, string> = {};
	if (opts.background) headers['X-Background'] = '1';
	if (method === 'GET') {
		for (const [k, v] of Object.entries(params)) url.searchParams.set(k, String(v));
	} else {
		body = new URLSearchParams(toStringRecord(params));
		headers['X-CSRF-Token'] = csrfToken();
	}
	// Dynamic ledger data must never come from the HTTP cache: the API sends no
	// cache headers, so a stale GET could otherwise outlive a write.
	const res = await fetch(url, { method, body: body ?? undefined, headers, cache: 'no-store' });
	if (res.headers.get('X-Session-Expired') === '1') {
		redirectToLogin();
		// Never settles: the page is navigating away, and rejecting would
		// flash a "Not logged in" error toast on the way out.
		return new Promise<T>(() => {});
	}
	const idleRemaining = res.headers.get('X-Session-Idle-Remaining');
	if (idleRemaining !== null) armIdleTimers(Number(idleRemaining));
	// Not every failure is our JSON: nginx's own 403/502/504 pages (bot
	// filter, PHP down) would otherwise surface as an opaque JSON SyntaxError
	// instead of what actually happened.
	let data: T & { error?: string };
	try {
		data = (await res.json()) as T & { error?: string };
	} catch {
		throw new Error(`${path}: HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ''}`);
	}
	if (!res.ok) throw new Error(data.error ?? `request failed (HTTP ${res.status})`);
	return data;
}

function requireEl<T extends HTMLElement>(id: string): T {
	const el = document.getElementById(id);
	if (!el) throw new Error(`missing element #${id}`);
	return el as T;
}

/**
 * A durable UI preference, or null when there is none. localStorage is a
 * convenience cache, never authoritative: it is absent entirely in some
 * privacy modes and can throw on quota, so every failure degrades to "nothing
 * stored" rather than propagating. A caller must treat the result as
 * untrusted input and validate it against live data before using it -- a
 * stale id (a business deleted since) is a normal outcome, not an error.
 *
 * No schema versioning: these are throwaway preferences whose worst case is
 * that the operator re-picks a control, and clearing them is always safe.
 */
function storedValue(key: string): string | null {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
}

/** Write a durable UI preference. Silent on failure, for the same reasons
 * storedValue() tolerates a read failure -- a preference is never worth
 * interrupting the interaction that set it. */
function setStoredValue(key: string, value: string): void {
	try {
		localStorage.setItem(key, value);
	} catch {
		/* storage unavailable or full: the preference simply does not persist */
	}
}

/** Shown on any failed write; every mutating call below routes its rejection
 * here. A persistent error toast (ts/toast.ts), not alert(): still
 * guaranteed to be seen (it never auto-dismisses, and gets its own close
 * button precisely because of that), but without blocking the rest of the
 * page the way a real dialog does. showToast() is defined in toast.ts,
 * loaded after this file (Assets::JS_FILES), but classic scripts share one
 * global scope and this only ever runs from an event handler well after
 * every file's top-level code has already executed, so the load-order
 * difference doesn't matter here. */
function reportError(err: unknown): void {
	showToast(err instanceof Error ? err.message : String(err), { kind: 'error', persist: true });
}

/**
 * Common shape behind every delegated row-level listener below: confirm
 * the event came from the expected control, then hand back its row.
 */
function rowTarget(e: Event, className: string): { row: HTMLTableRowElement; control: HTMLElement } | null {
	const control = e.target as HTMLElement;
	if (!control.classList.contains(className)) return null;
	const row = control.closest('tr');
	if (!row) return null;
	return { row, control };
}

/** Latest-wins gate for one DOM surface. A loader records a sequence number
 * when it starts and must not paint after an await unless it is still the
 * newest; every mutation that changes the same data bumps the gate up front,
 * so a load already in flight when a write starts can never repaint stale
 * rows over the result. */
class LoadGate {
	private seq = 0;

	begin(): number {
		return ++this.seq;
	}

	isCurrent(seq: number): boolean {
		return seq === this.seq;
	}
}
